import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, ExternalLink } from 'lucide-react';
import { Reveal, WordReveal } from '../components/Motion';
import { websiteDetailBySlug, websiteDetails } from '../mock/website-details';

const SectionTitle = ({ eyebrow, title }) => (
  <div className="mb-12">
    <span className="section-eyebrow">{eyebrow}</span>
    <h2 className="h-display mt-5 text-3xl md:text-5xl">{title}</h2>
  </div>
);

const NotFound = () => (
  <div className="px-6 md:px-12 lg:px-20 pt-40 pb-32 max-w-[1500px] mx-auto">
    <p className="section-eyebrow">404 — website not found</p>
    <h1 className="h-display text-5xl md:text-7xl mt-6">No such project.</h1>
    <Link
      to="/website-folio"
      className="btn-pill solid mt-10 inline-flex"
      data-testid="website-detail-back-404"
    >
      <ArrowLeft size={14}/> Back to website folio
    </Link>
  </div>
);

const Hero = ({ project }) => (
  <section className="px-6 md:px-12 lg:px-20 pt-32 pb-12 relative overflow-hidden">
    <div
      className="blob"
      style={{ width: 520, height: 520, background: 'var(--accent)', top: '-22%', right: '-12%', opacity: 0.12 }}
    />
    <div className="max-w-[1500px] mx-auto relative">
      <Link
        to="/website-folio"
        className="link-arrow inline-flex mb-10"
        data-testid="website-detail-back-link"
      >
        <span className="arrow rotate-180"><ArrowLeft size={14} /></span> Back to website folio
      </Link>
      <div className="text-[11px] uppercase tracking-widest text-accent">
        {project.cat} • {project.year}
      </div>
      <h1 className="h-display mt-6 text-[12vw] md:text-[7.5vw] leading-[0.95]">
        <WordReveal text={project.title} />
      </h1>
      <p className="mt-8 max-w-2xl text-muted text-[15px] leading-relaxed">{project.tagline}</p>
    </div>
  </section>
);

const InfoBar = ({ project }) => (
  <section className="px-6 md:px-12 lg:px-20 pb-12">
    <div className="max-w-[1500px] mx-auto card-prem p-8 md:p-10 grid md:grid-cols-12 gap-8">
      <Meta label="Role" value={project.role} className="md:col-span-3" />
      <Meta label="Duration" value={project.duration} className="md:col-span-2" />
      <Meta
        label="Scope"
        value={project.scope?.join(', ')}
        className="md:col-span-4"
      />
      <Meta
        label="Stack"
        value={project.stack?.join(' · ')}
        className="md:col-span-3"
      />
      {project.liveUrl && project.liveUrl !== '#' && (
        <div className="md:col-span-12 pt-2">
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-pill solid inline-flex"
            data-testid="website-detail-live-cta"
          >
            Visit live site <ExternalLink size={13} />
          </a>
        </div>
      )}
    </div>
  </section>
);

const Meta = ({ label, value, className = '' }) => {
  if (!value) return null;
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-widest text-dim">{label}</div>
      <div className="text-[14px] mt-2 leading-relaxed">{value}</div>
    </div>
  );
};

const CoverShot = ({ project }) => (
  <section className="px-6 md:px-12 lg:px-20 pb-16">
    <div className="max-w-[1500px] mx-auto">
      <Reveal>
        <figure className="case-study-showcase">
          <img
            src={project.cover}
            alt={`${project.title} — full screenshot`}
            className="case-study-showcase__img"
            loading="eager"
          />
        </figure>
      </Reveal>
    </div>
  </section>
);

const Overview = ({ project }) => (
  <section className="px-6 md:px-12 lg:px-20 py-16">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <SectionTitle eyebrow="Overview" title="The brief, the bet, the build." />
      </div>
      <div className="md:col-span-7">
        <p className="text-[15.5px] md:text-[17px] leading-[1.8] text-muted">{project.overview}</p>
        {project.highlights?.length > 0 && (
          <ul className="mt-10 space-y-4">
            {project.highlights.map((h, i) => (
              <li key={i} className="flex gap-4 text-[14.5px] leading-relaxed">
                <span className="text-accent mt-1">—</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </section>
);

const Screenshots = ({ project }) => {
  const shots = project.screenshots || [];
  if (shots.length === 0) return null;
  return (
    <section
      className="px-6 md:px-12 lg:px-20 py-16"
      data-testid="website-detail-screenshots"
    >
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle eyebrow="Showcase" title="In the wild." />
        <div className="case-study-showcase-stack">
          {shots.map((item, i) => {
            const src = typeof item === 'string' ? item : item.src;
            const alt = typeof item === 'string'
              ? `${project.title} — screen ${i + 1}`
              : (item.alt || item.caption || `Screen ${i + 1}`);
            const caption = typeof item === 'string' ? null : item.caption;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <figure className="case-study-showcase">
                  <img src={src} alt={alt} loading="lazy" className="case-study-showcase__img" />
                  {caption && (
                    <figcaption className="case-study-showcase__caption">{caption}</figcaption>
                  )}
                </figure>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const NextProject = ({ current }) => {
  const slugs = Object.keys(websiteDetails);
  const idx = slugs.indexOf(current);
  if (idx === -1) return null;
  const next = websiteDetails[slugs[(idx + 1) % slugs.length]];
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24 border-t" style={{ borderColor: 'var(--line)' }}>
      <div className="max-w-[1500px] mx-auto">
        <span className="section-eyebrow">Next up</span>
        <Link
          to={`/website/${next.slug}`}
          data-testid="website-detail-next"
          className="group block mt-6"
          data-cursor="hover"
        >
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-accent">
                {next.cat} • {next.year}
              </div>
              <h3 className="h-display mt-3 text-4xl md:text-6xl">{next.title}</h3>
            </div>
            <span className="link-arrow">
              View project <span className="arrow"><ArrowUpRight size={14}/></span>
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default function WebsiteDetail() {
  const { slug } = useParams();
  const project = websiteDetailBySlug(slug);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!project) return <NotFound />;

  return (
    <div data-testid="website-detail-page">
      <Hero project={project} />
      <InfoBar project={project} />
      <CoverShot project={project} />
      <Overview project={project} />
      <Screenshots project={project} />
      <NextProject current={project.slug} />
    </div>
  );
}
